import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { Table } from '@datum-group/isogd-openapi/lib/';
import { AxiosResponse } from 'axios';

type Query = Record<string, string | number | boolean | null>;

export interface TableSelectorServices {
  loadTableInfo(
    tableName: string,
    options?: any,
  ): Promise<AxiosResponse<Table>>;
  loadTables(
    page?: number,
    pageSize?: number,
    options?: any,
  ): Promise<AxiosResponse<{ results: Array<Table> }>>;
}

const PAGE = 1;
const PAGE_SIZE = 15;

export class TableSelector {
  @observable private _value?: Table = undefined;
  @observable private _options: Table[] = [];
  @observable private _search?: string = undefined;
  @observable private _isLoading = false;
  @observable private _optionsErrorMsg?: string = undefined;
  @observable private _valueErrorMsg?: string = undefined;
  @observable private _additionalQuery: Query;
  private _services: TableSelectorServices;

  constructor(services: TableSelectorServices, additionalQuery?: Query) {
    this._services = services;
    this._additionalQuery = { ...additionalQuery };

    makeObservable(this);

    this._loadOptions();
  }

  @computed get value(): Table | undefined {
    return this._value ? { ...this._value } : undefined;
  }

  @computed get search() {
    return this._search;
  }

  set search(value: string | undefined) {
    this._search = value;
    this._loadOptions();
  }

  @computed get options(): Table[] {
    return this._options.map((option) => ({ ...option }));
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get optionErrorMsg() {
    return this._optionsErrorMsg;
  }

  @computed get valueErrorMsg() {
    return this._valueErrorMsg;
  }

  @action setOption = (tableName?: string) => {
    this._valueErrorMsg = undefined;

    if (tableName === undefined) {
      this._value = undefined;

      return;
    }

    const option = this._options.find(
      (option) => option.table_name === tableName,
    );

    if (!option) throw new Error('Не найдена опция в списке опций');

    this._value = option;
  };

  @action setValue = async (tableName: string) => {
    await this._loadTableInfo(tableName);
  };

  reload = async () => {
    await this._loadOptions();

    if (this._value) await this._loadTableInfo(this._value.table_name);
  };

  @action private async _loadOptions() {
    this._isLoading = true;
    this._optionsErrorMsg = undefined;

    try {
      const query = this._buildQuery();
      const resp = await this._services.loadTables(PAGE, PAGE_SIZE, { query });
      runInAction(() => (this._options = resp.data.results));
    } catch (e) {
      runInAction(
        () => (this._optionsErrorMsg = 'Не удалось загрузить список опций'),
      );
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  }

  @action private async _loadTableInfo(tableName: string) {
    this._isLoading = true;
    this._valueErrorMsg = undefined;
    this._value = undefined;

    try {
      const resp = await this._services.loadTableInfo(tableName);
      runInAction(() => (this._value = resp.data));
    } catch (e) {
      runInAction(
        () => (this._valueErrorMsg = 'Таблица с таким именем не найдена'),
      );
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  }

  private _buildQuery() {
    const query: Query = { ...this._additionalQuery };

    if (this._search !== undefined) query['_search'] = this._search;

    if (this._value !== undefined) query['id__exact!'] = this._value.table_name;

    return query;
  }
}
